// ADR 5.9.26p §5 — the session, bootstrapped from the one token that survives a
// reload.
//
// A tab starts with nothing but `lingara.web.refresh.v1`, so the bootstrap is:
// refresh (the only endpoint that reports `email_verified`) → `GET /features`
// for the evaluated flags. Tier and trial state live *inside* those flags;
// `/features` carries no `email_verified` (`routes/features.rs:18`), which is
// why identity comes from the `AuthResponse` and never from there.
//
// Sign-out is the mirror image and its order is load-bearing: `/auth/logout`
// needs a valid bearer **and** the refresh token in its body
// (`refresh_tokens.rs:200-215`), so both are cleared only after the revoke has
// been attempted — and cleared whether or not it succeeded.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import { features, logout, refresh, type AuthResponse, type FeatureFlags } from '../api/auth'
import {
  readRefreshToken,
  signOutLocally,
  storeSession,
  SESSION_REFRESHED_EVENT,
  SIGNED_OUT_EVENT,
  type SessionIdentity,
} from './tokenStore'
import { useAccountStorageWipe } from './signOutWipe'
import { bootWithDeadline } from './bootDeadline'
import {
  clearAllTutorSessions,
  clearTutorSessionsForUser,
} from '../features/tutor/sessions/store'
import { NetworkUnreachableError } from '../offline/networkFailure'
import { resumedSession, useOfflineResume } from '../offline/offlineResume'

/** `loading` is the bootstrap window — a guard must not redirect during it. */
export type SessionStatus = 'loading' | 'signedIn' | 'signedOut'

export interface AuthSession {
  status: SessionStatus
  userId: string | null
  emailVerified: boolean
  flags: FeatureFlags | null
}

export interface AuthSessionActions {
  /** Take a session the server just minted (login, register or social). */
  adopt(response: AuthResponse): Promise<void>
  /** Revoke the token family, then forget it locally either way. */
  signOut(): Promise<void>
  /** Re-ask the one endpoint that reports `email_verified` (§5). */
  recheckVerification(): Promise<void>
  /**
   * Re-read `/features` into the live session — flags only, never identity.
   *
   * ADR 5.9.26s §4: a 404 from a gated route means this tab's flags are
   * known-stale (the flag went dark mid-session), and the surface that met it
   * has to hand the decision back to a gate that is reading current answers.
   */
  refreshFlags(): Promise<void>
}

export type AuthSessionContextValue = AuthSession & AuthSessionActions

const LOADING: AuthSession = { status: 'loading', userId: null, emailVerified: false, flags: null }
const SIGNED_OUT: AuthSession = {
  status: 'signedOut',
  userId: null,
  emailVerified: false,
  flags: null,
}

const AuthSessionContext = createContext<AuthSessionContextValue | null>(null)

/** Identity from the `AuthResponse`, flags from `/features` — never the reverse. */
async function sessionFor(response: AuthResponse): Promise<AuthSession> {
  storeSession(response)
  return {
    status: 'signedIn',
    userId: response.user_id,
    emailVerified: response.email_verified,
    flags: await features(),
  }
}

/** A refused refresh anywhere in the tab ends the session here too (AC4). */
function useSignedOutListener(apply: (next: AuthSession) => void): void {
  useEffect(() => {
    const onSignedOut = () => apply(SIGNED_OUT)
    window.addEventListener(SIGNED_OUT_EVENT, onSignedOut)
    return () => window.removeEventListener(SIGNED_OUT_EVENT, onSignedOut)
  }, [apply])
}

/**
 * The bootstrap chain: refresh (identity) → `/features` (flags).
 *
 * ADR 23.9.26v §2 — an unreachable step anywhere in the chain resumes from the
 * snapshot instead, and a resume that has no snapshot settles `SIGNED_OUT`
 * through `apply` rather than `signOutLocally`, so the refresh token survives
 * for the next online launch.
 *
 * ADR 24.9.26w — the no-token guard lives here, so a `null` from `refresh()`
 * always means the server refused a token this browser held. Nobody is then
 * signed in to this browser and `current.userId` is still `null`, so
 * `sessionTransition` cannot name the leaving account: the whole tutor store
 * goes instead. Unreachable and `/features` failures are not refusals.
 */
export async function bootstrapSession(
  clearAll: () => void | Promise<void> = clearAllTutorSessions,
): Promise<{ session: AuthSession; resumed: boolean }> {
  if (readRefreshToken() === null) return { session: SIGNED_OUT, resumed: false }
  let identity: SessionIdentity | null = null
  try {
    const response = await refresh()
    if (response === null) {
      void clearAll()
      return { session: SIGNED_OUT, resumed: false }
    }
    identity = { userId: response.user_id, emailVerified: response.email_verified }
    return { session: await sessionFor(response), resumed: false }
  } catch (error) {
    if (!(error instanceof NetworkUnreachableError)) return { session: SIGNED_OUT, resumed: false }
    const resumed = await resumedSession(identity)
    return resumed === null ? { session: SIGNED_OUT, resumed: false } : { session: resumed, resumed: true }
  }
}

function useBootstrap(
  apply: (next: AuthSession) => void, markResumed: () => void, patchIdentity: (identity: SessionIdentity) => void,
): void {
  useEffect(() => {
    let live = true
    const abort = new AbortController()
    // An anonymous tab is already `SIGNED_OUT` from the provider's initialiser
    // (ADR 2.10.26a §1), so this apply is a same-value update React bails out
    // of; it stays as the settle path. `bootstrapSession` repeats the check so
    // a `null` from `refresh()` there still always means a refusal (24.9.26w).
    if (readRefreshToken() === null) {
      apply(SIGNED_OUT)
    } else {
      // ADR 7.10.26ad §1 — raced against the boot deadline.
      void bootWithDeadline(() => bootstrapSession(), { apply, markResumed, patchIdentity, live: () => live }, abort.signal)
    }
    return () => {
      live = false
      abort.abort()
    }
  }, [apply, markResumed, patchIdentity])
}

/**
 * Every refresh reports `email_verified`, including one the 401 interceptor
 * triggered behind an unrelated request (§4) — so the identity is taken from
 * the store's event rather than only from the calls this context awaits.
 *
 * It patches a signed-in session and never creates one: during the bootstrap
 * the flags have not been fetched yet, and a half-built `signedIn` would let a
 * guard through a moment early.
 */
function useRefreshedIdentityListener(
  patch: (identity: SessionIdentity) => void,
): void {
  useEffect(() => {
    const onRefreshed = (event: Event) => patch((event as CustomEvent<SessionIdentity>).detail)
    window.addEventListener(SESSION_REFRESHED_EVENT, onRefreshed)
    return () => window.removeEventListener(SESSION_REFRESHED_EVENT, onRefreshed)
  }, [patch])
}

/**
 * Just the refresh: whatever it reports arrives through the store's event,
 * which is the same path an incidental refresh takes.
 *
 * ADR 23.9.26v §2 — `refresh()` can now reject while the server is
 * unreachable, and all three callers fire this and forget it (one of them on
 * window focus, exactly when an offline learner returns), so it resolves.
 */
async function recheckVerificationOnce(): Promise<void> {
  try {
    await refresh()
  } catch (error) {
    if (!(error instanceof NetworkUnreachableError)) throw error
  }
}

/**
 * Re-read `/features` into the live session — flags only, never identity.
 * Resolves `true` only when the flags were replaced, which is the signal
 * `useOfflineResume` clears its mark on. The snapshot is rewritten by that
 * hook's effect once the new flags settle (ADR 23.9.26v §2).
 */
async function refreshFlagsInto(
  setSession: (update: (current: AuthSession) => AuthSession) => void,
): Promise<boolean> {
  try {
    const fresh = await features()
    setSession((current) => (current.status === 'signedIn' ? { ...current, flags: fresh } : current))
    return true
  } catch {
    // A read that failed leaves the old answer standing: stale flags are
    // what this tab already had, and a null one would darken every gate.
    return false
  }
}

export function AuthSessionProvider({ children }: { children: ReactNode }) {
  // ADR 2.10.26a §1 — a token-less tab is settled before its first render: the
  // read is synchronous, so the painted first commit is the signed-out page
  // rather than a skeleton the consent strip then pushes down.
  const [session, setSession] = useState<AuthSession>(() =>
    readRefreshToken() === null ? SIGNED_OUT : LOADING,
  )
  const apply = useSessionApply(setSession)
  const patchIdentity = useCallback((identity: SessionIdentity) => {
    setSession((current) =>
      current.status === 'signedIn'
        ? { ...current, userId: identity.userId, emailVerified: identity.emailVerified }
        : current,
    )
  }, [])

  const refreshFlags = useCallback(() => refreshFlagsInto(setSession), [])
  const markResumed = useOfflineResume(session, refreshFlags)

  useBootstrap(apply, markResumed, patchIdentity)
  useSignedOutListener(apply)
  useAccountStorageWipe()
  useRefreshedIdentityListener(patchIdentity)

  const value = useMemo<AuthSessionContextValue>(
    () => ({
      ...session,
      adopt: async (response) => apply(await sessionFor(response)),
      signOut: async () => {
        try {
          await logout()
        } catch {
          // A revoke the server never saw still ends the session in this tab;
          // the token family expires on its own (§5).
        }
        signOutLocally()
      },
      recheckVerification: recheckVerificationOnce,
      refreshFlags: async () => {
        await refreshFlags()
      },
    }),
    [session, apply, refreshFlags],
  )

  return <AuthSessionContext.Provider value={value}>{children}</AuthSessionContext.Provider>
}

function useSessionApply(setSession: (update: (current: AuthSession) => AuthSession) => void) {
  return useCallback((next: AuthSession) => setSession(
    (current) => sessionTransition(current, next),
  ), [setSession])
}

/** One exit transition clears exactly the account that is leaving. */
export function sessionTransition(
  current: AuthSession,
  next: AuthSession,
  clear: (userId: string) => void | Promise<void> = clearTutorSessionsForUser,
): AuthSession {
  if (next.status === 'signedOut' && current.userId !== null) void clear(current.userId)
  return next
}

/**
 * ADR 9.10.26a §2 — is a verified learner signed in? `false` outside a
 * provider rather than a throw: the cloud mic it gates is offered to nobody
 * there, which is the safe answer.
 */
export function useEmailVerified(): boolean {
  return useContext(AuthSessionContext)?.emailVerified ?? false
}

export function useAuthSession(): AuthSessionContextValue {
  const value = useContext(AuthSessionContext)
  if (value === null) {
    throw new Error('useAuthSession must be used inside an AuthSessionProvider')
  }
  return value
}
